import type { GamePreset, OcrModelResource } from '@/presets/types'
import { performanceDiagnostics } from './performance-diagnostics'
import {
  OCR_DETECTION_MODEL,
  OCR_ORT_MJS,
  OCR_ORT_WASM,
  OCR_RECOGNITION_MODEL,
  type OcrCdnResource,
} from '@/services/ocr-resource-manifest'
import {
  contentLengthExceedsLimit,
  isOcrResourceLoadError,
  OcrResourceLoadError,
  verifySha256,
} from '@/services/ocr-resource-integrity'

export type OcrModelKey = 'detection' | 'recognition'
export { isOcrResourceLoadError, OcrResourceLoadError }

export interface OcrCacheProgress {
  readonly model: OcrModelKey
  readonly progress: number
  readonly fromCache: boolean
}

export interface OcrCachePreparationResult {
  readonly cacheDegraded: boolean
  readonly usedTemporaryResources: boolean
  releaseTemporaryResources(): void
}

export interface OcrResourceCacheOptions {
  readonly connectionTimeoutMs?: number
  readonly readStallTimeoutMs?: number
  readonly cacheOperationTimeoutMs?: number
}

export const OCR_MODEL_CACHE_NAME = 'maple-log-ocr-models-v4'
export const OCR_RUNTIME_CACHE_NAME = 'maple-log-ocr-ort-v1'
export const OCR_WORKER_RUNTIME_CACHE_NAME = 'maple-log-ocr-runtime-v3'
export const OCR_CDN_CACHE_NAME = 'maple-log-cdn-v2'
export const OCR_LEGACY_MODEL_CACHE_NAME = 'maple-log-ocr-models-v3'
export const OCR_LEGACY_RUNTIME_CACHE_NAME = 'maple-log-ocr-runtime-v2'
export const OCR_RESOURCE_CACHE_NAMES = [
  OCR_MODEL_CACHE_NAME,
  OCR_RUNTIME_CACHE_NAME,
  OCR_WORKER_RUNTIME_CACHE_NAME,
  OCR_CDN_CACHE_NAME,
  OCR_LEGACY_MODEL_CACHE_NAME,
  OCR_LEGACY_RUNTIME_CACHE_NAME,
] as const
const LEGACY_MODEL_VERSION_PARAM = '__maple_log_model_version'
const DEFAULT_CONNECTION_TIMEOUT_MS = 15_000
const DEFAULT_READ_STALL_TIMEOUT_MS = 20_000
const DEFAULT_CACHE_OPERATION_TIMEOUT_MS = 10_000

interface TemporaryResource {
  readonly bytes: Uint8Array<ArrayBuffer>
  readonly contentType: string
  readonly sourceUrl: string
  readonly token: symbol
}

interface DownloadedResource {
  readonly bytes: Uint8Array<ArrayBuffer>
  readonly contentType: string
  readonly sourceUrl: string
}

interface OperationSuccess<T> {
  readonly ok: true
  readonly value: T
}

interface OperationFailure {
  readonly ok: false
  readonly error: unknown
  readonly timedOut: boolean
}

type OperationResult<T> = OperationSuccess<T> | OperationFailure

const temporaryResources = new Map<string, TemporaryResource>()

export interface ResolvedOcrModels {
  readonly models: GamePreset['remoteResources']['ocrModels']
  readonly wasmPaths: { readonly mjs: string; readonly wasm: string }
  release(): void
}

export async function ensureOcrModelsCached(
  models: GamePreset['remoteResources']['ocrModels'],
  onProgress: (progress: OcrCacheProgress) => void,
  options: OcrResourceCacheOptions = {},
): Promise<OcrCachePreparationResult> {
  const resources = managedResources(models)
  const cacheTimeoutMs = options.cacheOperationTimeoutMs ?? DEFAULT_CACHE_OPERATION_TIMEOUT_MS
  const temporaryLeases: { readonly key: string; readonly token: symbol }[] = []
  let cacheDegraded = false
  const cachesByKind = await openManagedCaches(cacheTimeoutMs)
  if (!cachesByKind.models || !cachesByKind.runtime) cacheDegraded = true

  try {
    for (let index = 0; index < resources.length; index += 1) {
      const resource = resources[index]!
      const cache = cacheFor(resource, cachesByKind)
      const cacheRequest = createCacheRequest(resource)
      let cached: DownloadedResource | null = null

      if (cache) {
        const verified = await readVerifiedCacheEntry(cache, cacheRequest, resource, cacheTimeoutMs)
        if (verified.ok) cached = verified.value
        else if (verified.degraded) {
          cachesByKind.models = resource.kind === 'model' ? null : cachesByKind.models
          cachesByKind.runtime = resource.kind === 'runtime' ? null : cachesByKind.runtime
          cacheDegraded = true
        }
      }

      if (!cached) {
        cached = await migrateLegacyResource(resource, cacheFor(resource, cachesByKind), cacheTimeoutMs)
      }

      if (cached) {
        onProgress({
          model: progressModel(resource),
          progress: (index + 1) / resources.length,
          fromCache: true,
        })
        continue
      }

      const downloaded = await downloadVerifiedResource(resource, (resourceProgress) => {
        onProgress({
          model: progressModel(resource),
          progress: (index + resourceProgress) / resources.length,
          fromCache: false,
        })
      }, options)
      const lease = retainTemporary(resource, downloaded)
      temporaryLeases.push(lease)

      const writable = cacheFor(resource, cachesByKind)
      if (writable) {
        const cacheWrite = await settleOperation(
          writable.put(cacheRequest, responseFromDownload(downloaded)),
          cacheTimeoutMs,
          'cache write',
        )
        if (!cacheWrite.ok) {
          if (resource.kind === 'model') cachesByKind.models = null
          else cachesByKind.runtime = null
          cacheDegraded = true
        } else {
          releaseTemporaryLeases([lease])
          temporaryLeases.pop()
        }
      }
    }

    if (cachesByKind.models) {
      const cleanup = await settleOperation(
        removeOutdatedEntries(cachesByKind.models, modelResources(models).map((resource) => resourceCacheKey(resource))),
        cacheTimeoutMs,
        'cache cleanup',
      )
      if (!cleanup.ok) cacheDegraded = true
    }
    if (cachesByKind.runtime) {
      const cleanup = await settleOperation(
        removeOutdatedEntries(cachesByKind.runtime, runtimeResources().map((resource) => resourceCacheKey(resource))),
        cacheTimeoutMs,
        'cache cleanup',
      )
      if (!cleanup.ok) cacheDegraded = true
    }
  } catch (error) {
    releaseTemporaryLeases(temporaryLeases)
    throw error
  }

  let released = false
  return {
    cacheDegraded,
    usedTemporaryResources: temporaryLeases.length > 0,
    releaseTemporaryResources() {
      if (released) return
      released = true
      releaseTemporaryLeases(temporaryLeases)
    },
  }
}

export async function clearOcrResourceCaches(): Promise<void> {
  if (typeof globalThis.caches === 'undefined') return
  await Promise.all(OCR_RESOURCE_CACHE_NAMES.map((name) => caches.delete(name)))
}

export async function resolveCachedOcrModels(
  models: GamePreset['remoteResources']['ocrModels'],
  options: OcrResourceCacheOptions = {},
): Promise<ResolvedOcrModels> {
  const cacheTimeoutMs = options.cacheOperationTimeoutMs ?? DEFAULT_CACHE_OPERATION_TIMEOUT_MS
  const cachesByKind = await openManagedCaches(cacheTimeoutMs)
  const objectUrls: string[] = []
  const urlDiagnostics: (() => void)[] = []
  const releaseUrls = () => {
    objectUrls.forEach((url) => URL.revokeObjectURL(url))
    urlDiagnostics.forEach(release => release())
  }
  const resolvedModels: Partial<Record<OcrModelKey, OcrModelResource>> = {}
  const wasmPaths: { mjs?: string; wasm?: string } = {}

  try {
    for (const resource of managedResources(models)) {
      const blob = await resolveResourceBlob(resource, cacheFor(resource, cachesByKind), options)
      if (typeof URL.createObjectURL !== 'function') {
        throw new OcrResourceLoadError('OCR resource could not be isolated for execution')
      }
      const objectUrl = URL.createObjectURL(blob)
      objectUrls.push(objectUrl)
      urlDiagnostics.push(performanceDiagnostics.acquire('ocr-model-urls'))
      if (resource.id === 'detection' || resource.id === 'recognition') {
        resolvedModels[resource.id] = { ...resourceToModel(resource), url: objectUrl }
      } else if (resource.id === 'ort-mjs') {
        wasmPaths.mjs = objectUrl
      } else if (resource.id === 'ort-wasm') {
        wasmPaths.wasm = objectUrl
      }
    }
  } catch (error) {
    releaseUrls()
    throw error
  }

  if (!resolvedModels.detection || !resolvedModels.recognition || !wasmPaths.mjs || !wasmPaths.wasm) {
    releaseUrls()
    throw new OcrResourceLoadError('OCR resource could not be isolated for execution')
  }

  let released = false
  return {
    models: {
      detection: resolvedModels.detection,
      recognition: resolvedModels.recognition,
    },
    wasmPaths: { mjs: wasmPaths.mjs, wasm: wasmPaths.wasm },
    release() {
      if (released) return
      released = true
      releaseUrls()
    },
  }
}

export function modelCacheKey(resource: Pick<OcrModelResource, 'sha256'> & { id?: string; name?: string }): string {
  return resourceCacheKey({
    id: resource.id ?? resource.name ?? 'model',
    sha256: resource.sha256,
  })
}

export function resourceCacheKey(resource: Pick<OcrCdnResource, 'id' | 'sha256'>): string {
  return `https://maple-log.resources/ocr/${encodeURIComponent(resource.id)}/${resource.sha256.toLowerCase()}`
}

function managedResources(models: GamePreset['remoteResources']['ocrModels']): readonly ManagedResource[] {
  return [...modelResources(models), ...runtimeResources()]
}

function modelResources(models: GamePreset['remoteResources']['ocrModels']): readonly ManagedResource[] {
  return [
    toManagedResource('model', {
      id: 'detection',
      name: models.detection.name,
      version: models.detection.version,
      url: models.detection.url,
      fallbackUrls: models.detection.fallbackUrls ?? [],
      sha256: models.detection.sha256,
      maxBytes: models.detection.maxBytes,
      contentType: OCR_DETECTION_MODEL.contentType,
      legacyCacheUrls: OCR_DETECTION_MODEL.legacyCacheUrls,
      legacyVersions: OCR_DETECTION_MODEL.legacyVersions,
    }),
    toManagedResource('model', {
      id: 'recognition',
      name: models.recognition.name,
      version: models.recognition.version,
      url: models.recognition.url,
      fallbackUrls: models.recognition.fallbackUrls ?? [],
      sha256: models.recognition.sha256,
      maxBytes: models.recognition.maxBytes,
      contentType: OCR_RECOGNITION_MODEL.contentType,
      legacyCacheUrls: OCR_RECOGNITION_MODEL.legacyCacheUrls,
      legacyVersions: OCR_RECOGNITION_MODEL.legacyVersions,
    }),
  ]
}

function runtimeResources(): readonly ManagedResource[] {
  return [
    toManagedResource('runtime', OCR_ORT_MJS),
    toManagedResource('runtime', OCR_ORT_WASM),
  ]
}

interface ManagedResource extends OcrCdnResource {
  readonly kind: 'model' | 'runtime'
}

function toManagedResource(kind: 'model' | 'runtime', resource: OcrCdnResource): ManagedResource {
  return { ...resource, kind }
}

function progressModel(resource: ManagedResource): OcrModelKey {
  return resource.id === 'detection' ? 'detection' : 'recognition'
}

function resourceToModel(resource: ManagedResource): OcrModelResource {
  return {
    name: resource.name,
    url: resource.url,
    fallbackUrls: resource.fallbackUrls,
    version: resource.version,
    sha256: resource.sha256,
    maxBytes: resource.maxBytes,
  }
}

function createCacheRequest(resource: Pick<OcrCdnResource, 'id' | 'sha256'>): Request {
  return new Request(resourceCacheKey(resource), { credentials: 'omit' })
}

async function openManagedCaches(timeoutMs: number): Promise<{ models: Cache | null; runtime: Cache | null }> {
  return {
    models: await openNamedCache(OCR_MODEL_CACHE_NAME, timeoutMs),
    runtime: await openNamedCache(OCR_RUNTIME_CACHE_NAME, timeoutMs),
  }
}

function cacheFor(resource: ManagedResource, cachesByKind: { models: Cache | null; runtime: Cache | null }): Cache | null {
  return resource.kind === 'model' ? cachesByKind.models : cachesByKind.runtime
}

async function openNamedCache(name: string, timeoutMs: number): Promise<Cache | null> {
  if (typeof globalThis.caches === 'undefined') return null
  const opened = await settleOperation(caches.open(name), timeoutMs, 'cache open')
  return opened.ok ? opened.value : null
}

async function readVerifiedCacheEntry(
  cache: Cache,
  request: Request,
  resource: Pick<OcrCdnResource, 'sha256' | 'maxBytes' | 'contentType' | 'url'>,
  timeoutMs: number,
): Promise<{ ok: true; value: DownloadedResource } | { ok: false; degraded: boolean }> {
  const match = await settleOperation(cache.match(request), timeoutMs, 'cache read')
  if (!match.ok) return { ok: false, degraded: true }
  if (!match.value) return { ok: false, degraded: false }
  const body = await settleOperation(match.value.arrayBuffer(), timeoutMs, 'cache response read')
  if (!body.ok) {
    await settleOperation(cache.delete(request), timeoutMs, 'cache delete').catch(() => {})
    return { ok: false, degraded: true }
  }
  const bytes = new Uint8Array(body.value)
  if (bytes.byteLength > resource.maxBytes || !await verifySha256(bytes, resource.sha256)) {
    await settleOperation(cache.delete(request), timeoutMs, 'cache delete')
    return { ok: false, degraded: false }
  }
  return {
    ok: true,
    value: {
      bytes,
      contentType: match.value.headers.get('content-type') ?? resource.contentType,
      sourceUrl: match.value.headers.get('x-maple-log-source') ?? resource.url,
    },
  }
}

async function migrateLegacyResource(
  resource: ManagedResource,
  destination: Cache | null,
  timeoutMs: number,
): Promise<DownloadedResource | null> {
  if (typeof globalThis.caches === 'undefined') return null
  for (const [name, urls] of legacyLookupTargets(resource)) {
    const cache = await openNamedCache(name, timeoutMs)
    if (!cache) continue
    for (const url of urls) {
      const match = await settleOperation(cache.match(new Request(url, { credentials: 'omit' })), timeoutMs, 'cache read')
      if (!match.ok || !match.value) continue
      const body = await settleOperation(match.value.arrayBuffer(), timeoutMs, 'cache response read')
      if (!body.ok) continue
      const bytes = new Uint8Array(body.value)
      if (bytes.byteLength > resource.maxBytes || !await verifySha256(bytes, resource.sha256)) {
        if (name !== OCR_CDN_CACHE_NAME) await settleOperation(cache.delete(url), timeoutMs, 'cache delete')
        continue
      }
      const downloaded: DownloadedResource = {
        bytes,
        contentType: match.value.headers.get('content-type') ?? resource.contentType,
        sourceUrl: url,
      }
      if (destination) {
        const written = await settleOperation(
          destination.put(createCacheRequest(resource), responseFromDownload(downloaded)),
          timeoutMs,
          'cache write',
        )
        if (!written.ok) return downloaded
      }
      if (name === OCR_CDN_CACHE_NAME) {
        if (resource.kind === 'runtime') await settleOperation(cache.delete(url), timeoutMs, 'cache delete')
      } else {
        await settleOperation(cache.delete(url), timeoutMs, 'cache delete')
      }
      return downloaded
    }
  }
  return null
}

function legacyLookupTargets(resource: ManagedResource): readonly (readonly [string, readonly string[]])[] {
  const sourceUrls = [...new Set([resource.url, ...(resource.legacyCacheUrls ?? [])])]
  const modelKeys = sourceUrls.flatMap((url) => {
    const versions = [...new Set([resource.version, ...(resource.legacyVersions ?? [])])]
    return versions.map((version) => {
      const parsed = new URL(url)
      parsed.searchParams.set(LEGACY_MODEL_VERSION_PARAM, version)
      return parsed.href
    })
  })
  if (resource.kind === 'model') {
    return [[OCR_LEGACY_MODEL_CACHE_NAME, modelKeys]]
  }
  return [
    [OCR_CDN_CACHE_NAME, sourceUrls],
    [OCR_LEGACY_RUNTIME_CACHE_NAME, sourceUrls],
  ]
}

async function resolveResourceBlob(
  resource: ManagedResource,
  cache: Cache | null,
  options: OcrResourceCacheOptions,
): Promise<Blob> {
  const cacheTimeoutMs = options.cacheOperationTimeoutMs ?? DEFAULT_CACHE_OPERATION_TIMEOUT_MS
  if (cache) {
    const verified = await readVerifiedCacheEntry(cache, createCacheRequest(resource), resource, cacheTimeoutMs)
    if (verified.ok) return new Blob([verified.value.bytes], { type: verified.value.contentType })
  }
  const migrated = await migrateLegacyResource(resource, cache, cacheTimeoutMs)
  if (migrated) return new Blob([migrated.bytes], { type: migrated.contentType })
  const temporary = temporaryResources.get(resourceCacheKey(resource))
  if (temporary) {
    if (temporary.bytes.byteLength > resource.maxBytes || !await verifySha256(temporary.bytes, resource.sha256)) {
      temporaryResources.delete(resourceCacheKey(resource))
    } else {
      return new Blob([temporary.bytes], { type: temporary.contentType })
    }
  }
  const downloaded = await downloadVerifiedResource(resource, () => {}, options)
  if (cache) {
    await settleOperation(cache.put(createCacheRequest(resource), responseFromDownload(downloaded)), cacheTimeoutMs, 'cache write')
  }
  return new Blob([downloaded.bytes], { type: downloaded.contentType })
}

async function downloadVerifiedResource(
  resource: OcrCdnResource,
  onProgress: (progress: number) => void,
  options: OcrResourceCacheOptions,
): Promise<DownloadedResource> {
  const urls = [resource.url, ...resource.fallbackUrls]
  const failures: string[] = []
  let reportedProgress = 0

  for (const url of urls) {
    const controller = new AbortController()
    try {
      const fetched = await settleOperation(
        fetch(new Request(url, {
          cache: 'no-store',
          credentials: 'omit',
          mode: 'cors',
          signal: controller.signal,
        })),
        options.connectionTimeoutMs ?? DEFAULT_CONNECTION_TIMEOUT_MS,
        'connection',
        () => controller.abort(),
      )
      if (!fetched.ok) {
        failures.push(`${url}: ${errorMessage(fetched.error)}`)
        continue
      }
      if (!fetched.value.ok) {
        failures.push(`${url}: HTTP ${fetched.value.status}`)
        continue
      }
      if (contentLengthExceedsLimit(fetched.value.headers.get('content-length'), resource.maxBytes)) {
        await fetched.value.body?.cancel()
        failures.push(`${url}: response exceeded size limit`)
        continue
      }

      const body = await consumeResponse(
        fetched.value,
        resource.maxBytes,
        (progress) => {
          reportedProgress = Math.max(reportedProgress, progress)
          onProgress(reportedProgress)
        },
        options.readStallTimeoutMs ?? DEFAULT_READ_STALL_TIMEOUT_MS,
        controller,
      )
      if (!await verifySha256(body.bytes, resource.sha256)) {
        failures.push(`${url}: integrity check failed`)
        continue
      }
      return { ...body, sourceUrl: url }
    } catch (error) {
      failures.push(`${url}: ${errorMessage(error)}`)
    } finally {
      controller.abort()
    }
  }

  throw new OcrResourceLoadError(`OCR resource request failed (${failures.join('; ')})`)
}

async function consumeResponse(
  response: Response,
  maxBytes: number,
  onProgress: (progress: number) => void,
  stallTimeoutMs: number,
  controller: AbortController,
): Promise<Pick<DownloadedResource, 'bytes' | 'contentType'>> {
  const total = Number(response.headers.get('content-length')) || 0
  const contentType = response.headers.get('content-type') ?? 'application/octet-stream'
  if (!response.body) {
    const body = await settleOperation(response.arrayBuffer(), stallTimeoutMs, 'response read', () => controller.abort())
    if (!body.ok) throw body.error
    if (body.value.byteLength > maxBytes) throw new OcrResourceLoadError('OCR resource exceeded size limit')
    onProgress(1)
    return { bytes: new Uint8Array(body.value), contentType }
  }

  const reader = response.body.getReader()
  const chunks: Uint8Array<ArrayBuffer>[] = []
  let loaded = 0
  while (true) {
    const read = await settleOperation(reader.read(), stallTimeoutMs, 'response read', () => {
      controller.abort()
      void reader.cancel().catch(() => {})
    })
    if (!read.ok) throw read.error
    if (read.value.done) break
    const chunk = new Uint8Array(read.value.value.byteLength)
    chunk.set(read.value.value)
    loaded += chunk.byteLength
    if (loaded > maxBytes) {
      controller.abort()
      void reader.cancel().catch(() => {})
      throw new OcrResourceLoadError('OCR resource exceeded size limit')
    }
    chunks.push(chunk)
    if (total > 0) onProgress(Math.min(1, loaded / total))
  }
  onProgress(1)
  const bytes = new Uint8Array(loaded)
  let offset = 0
  for (const chunk of chunks) {
    bytes.set(chunk, offset)
    offset += chunk.byteLength
  }
  return { bytes, contentType }
}

function responseFromDownload(downloaded: DownloadedResource): Response {
  return new Response(downloaded.bytes, {
    status: 200,
    headers: {
      'content-length': String(downloaded.bytes.byteLength),
      'content-type': downloaded.contentType,
      'x-maple-log-source': downloaded.sourceUrl,
    },
  })
}

function retainTemporary(
  resource: Pick<OcrCdnResource, 'id' | 'sha256'>,
  downloaded: DownloadedResource,
): { readonly key: string; readonly token: symbol } {
  const key = resourceCacheKey(resource)
  const token = Symbol(key)
  temporaryResources.set(key, { ...downloaded, token })
  return { key, token }
}

function releaseTemporaryLeases(leases: readonly { readonly key: string; readonly token: symbol }[]): void {
  for (const lease of leases) {
    if (temporaryResources.get(lease.key)?.token === lease.token) {
      temporaryResources.delete(lease.key)
    }
  }
}

async function removeOutdatedEntries(cache: Cache, currentUrls: readonly string[]): Promise<void> {
  const current = new Set(currentUrls)
  const requests = await cache.keys()
  await Promise.all(requests
    .filter((request) => !current.has(request.url))
    .map((request) => cache.delete(request)))
}

function settleOperation<T>(
  operation: Promise<T>,
  timeoutMs: number,
  label: string,
  onTimeout?: () => void,
): Promise<OperationResult<T>> {
  return new Promise((resolve) => {
    let settled = false
    const timer = globalThis.setTimeout(() => {
      if (settled) return
      settled = true
      onTimeout?.()
      resolve({ ok: false, error: new Error(`${label} timed out`), timedOut: true })
    }, Math.max(0, timeoutMs))

    operation.then(
      (value) => {
        if (settled) return
        settled = true
        globalThis.clearTimeout(timer)
        resolve({ ok: true, value })
      },
      (error: unknown) => {
        if (settled) return
        settled = true
        globalThis.clearTimeout(timer)
        resolve({ ok: false, error, timedOut: false })
      },
    )
  })
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'network error'
}
